/**
 * Moraze Coins (M.C.) Storefront Loyalty Controller
 * Coordinates Customer Accounts, Cart Widgets, and secure discount redemptions
 */

class MorazeCoinsController {
  constructor() {
    if (localStorage.getItem('moraze_coins_disabled_globally') === 'true') {
      console.log('🪙 Moraze Coins is disabled by Admin.');
      return;
    }
    this.customerId = window.MorazeCoinsConfig?.customerId || null;
    this.proxyUrl = '/apps/moraze-coins/api';
    
    // Check if customer session has changed (logins, logouts, or account switches)
    const cachedCustomerId = localStorage.getItem('moraze_coins_customer_id');
    if (this.customerId !== cachedCustomerId) {
      localStorage.removeItem('moraze_coins_balance');
      if (this.customerId) {
        localStorage.setItem('moraze_coins_customer_id', this.customerId);
      } else {
        localStorage.removeItem('moraze_coins_customer_id');
      }
    }
    
    // Initialize balance from localStorage or config
    const localBalance = localStorage.getItem('moraze_coins_balance');
    if (localBalance !== null) {
      this.balance = parseInt(localBalance, 10);
    } else {
      this.balance = window.MorazeCoinsConfig?.coins || 0;
      localStorage.setItem('moraze_coins_balance', this.balance);
    }
    this.currencyValue = this.balance / 100;
    
    this.init();
  }

  // Initialise Guest loyalty flows
  initGuest() {
    console.log('🪙 Moraze Coins Guest Mode Active.');
    this.injectModalStyles();
    this.bindLoginListeners();
  }

  async init() {
    console.log('🪙 Moraze Coins Active. Customer logged in:', this.customerId);
    
    // 1. If on Customer Account Page, render Loyalty Tab Content
    if (document.getElementById('moraze-coins-account-root')) {
      await this.loadAccountDashboard();
    }

    // 2. Initialize milestones component if present
    if (document.getElementById('mcm-root')) {
      this.initMilestones();
    }

    // 3. Bind checkout interception sitewide
    this.bindCheckoutInterception();
  }

  initMilestones() {
    console.log('🪙 Moraze Coins Milestones Component Active.');
    
    // Bind click events on "Add to Cart" button
    this.bindMilestoneClickEvents();

    // Hook into window.openCartDrawer to refresh state on open
    if (typeof window.openCartDrawer === 'function') {
      const originalOpen = window.openCartDrawer;
      window.openCartDrawer = () => {
        originalOpen();
        this.fetchLiveBalanceAndSync();
      };
    }

    // Hook into cart drawer updates (e.g. manual removal or quantity change)
    document.addEventListener('cart-drawer:updated', () => {
      this.fetchLiveBalanceAndSync();
    });

    // Run initial sync
    this.fetchLiveBalanceAndSync();
  }

  async fetchLiveBalanceAndSync() {
    const customerId = window.MorazeCoinsConfig?.customerId || this.customerId;
    const rootUrl = window.Shopify?.routes?.root || '/';

    // Fetch current cart state
    let cart = null;
    try {
      const cartRes = await fetch(rootUrl + 'cart.js');
      cart = await cartRes.json();
    } catch(e) {
      console.warn('[Moraze Coins Milestones] Failed to fetch cart:', e);
    }

    if (!customerId) {
      // Guest mode
      this.balance = 0;
      this.updateMilestonesUI(0, cart);
      return;
    }

    const BASE_URL = 'BACKEND_URL_HERE'; // Replace with backend URL if needed
    
    // Try to fetch balance from loyalty backend
    try {
      const res = await fetch(`${BASE_URL}/balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId })
      });
      const data = await res.json();
      if (data && data.coins !== undefined) {
        this.balance = data.coins;
        window.MorazeCoinsConfig.coins = data.coins; // sync config
        localStorage.setItem('moraze_coins_balance', data.coins);
      }
    } catch(e) {
      console.warn('[Moraze Coins Milestones] Failed to fetch live balance, using cached value:', e);
      const localBalance = localStorage.getItem('moraze_coins_balance');
      if (localBalance !== null) {
        this.balance = parseInt(localBalance, 10);
      } else {
        this.balance = window.MorazeCoinsConfig?.coins || 0;
        localStorage.setItem('moraze_coins_balance', this.balance);
      }
    }

    this.updateMilestonesUI(this.balance, cart);
  }

  updateMilestonesUI(coins, cart) {
    const mcmRoot = document.getElementById('mcm-root');
    if (!mcmRoot) return;

    // Show the container
    mcmRoot.style.display = 'block';

    const cards = mcmRoot.querySelectorAll('.mcm-card');
    cards.forEach(card => {
      const threshold = parseInt(card.getAttribute('data-threshold'), 10);
      const variantId = parseInt(card.getAttribute('data-variant-id'), 10);
      const btn = card.querySelector('.mcm-btn');
      const caption = card.querySelector('.mcm-caption');

      // Check if gift variant is already in the cart
      let isAlreadyInCart = false;
      if (cart && cart.items) {
        isAlreadyInCart = cart.items.some(item => {
          return item.variant_id === variantId && item.properties && item.properties._free_gift === 'true';
        });
      }

      const isUnlocked = coins >= threshold;

      if (isUnlocked) {
        card.classList.add('unlocked');
        if (isAlreadyInCart) {
          // Added state
          btn.className = 'mcm-btn mcm-btn-added';
          btn.disabled = true;
          btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Added</span>
          `;
          caption.textContent = 'Added to your cart successfully!';
        } else {
          // Unlocked state (ready to add)
          btn.className = 'mcm-btn mcm-btn-unlocked';
          btn.disabled = false;
          btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Add to Cart</span>
          `;
          caption.textContent = 'Free gift unlocked — add it to your cart.';
        }
      } else {
        // Locked state
        card.classList.remove('unlocked');
        btn.className = 'mcm-btn mcm-btn-locked';
        btn.disabled = true;
        btn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <span>Add Now</span>
          <span class="mcm-coin-badge">🪙 ${threshold}</span>
        `;
        caption.textContent = 'This item is locked. Unlock to add now.';
      }
    });
  }

  bindMilestoneClickEvents() {
    if (this._milestonesListenerBound) return;
    this._milestonesListenerBound = true;

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.mcm-btn-unlocked');
      if (!btn) return;

      const card = btn.closest('.mcm-card');
      if (!card) return;

      const variantId = parseInt(card.getAttribute('data-variant-id'), 10);
      const threshold = parseInt(card.getAttribute('data-threshold'), 10) || 500;
      if (!variantId) return;

      btn.disabled = true;
      const originalHTML = btn.innerHTML;
      btn.innerHTML = 'Adding...';

      const rootUrl = window.Shopify?.routes?.root || '/';
      
      try {
        const res = await fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                '_free_gift': 'true',
                'Gift Status': 'Milestone',
                '_coins_cost': threshold
              }
            }]
          })
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.description || data.message || 'Failed to add gift to cart');
        }

        // Success - update cart drawer
        if (typeof window.updateCartDrawer === 'function') {
          window.updateCartDrawer();
        }

        // Trigger balance and milestone check
        await this.fetchLiveBalanceAndSync();

      } catch(err) {
        console.error('[Moraze Coins Milestones] Error adding gift:', err);
        alert('Could not add free gift: ' + err.message);
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    });
  }

  bindCheckoutInterception() {
    if (!this.customerId) return;

    // Use event delegation on document click
    document.addEventListener('click', async (e) => {
      const checkoutBtn = e.target.closest('a[href*="/checkout"], button[name="checkout"], input[type="submit"][name="checkout"], .cd-checkout-btn, [onclick*="/checkout"]');
      if (!checkoutBtn) return;

      // Prevent duplicate processing
      if (checkoutBtn.dataset.coinsProcessed === 'true') {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Temporarily disable the button to prevent double-clicks
      const originalHTML = checkoutBtn.innerHTML;
      const isInput = checkoutBtn.tagName === 'INPUT';
      if (isInput) {
        checkoutBtn.value = 'Processing...';
      } else {
        checkoutBtn.innerHTML = 'Processing...';
      }
      checkoutBtn.disabled = true;

      try {
        await this.handleCheckoutCoinsUpdate();
      } catch (err) {
        console.error('[Moraze Coins] Error updating coins on checkout:', err);
      }

      // Mark as processed and resume checkout
      checkoutBtn.dataset.coinsProcessed = 'true';
      checkoutBtn.disabled = false;
      if (isInput) {
        checkoutBtn.value = originalHTML;
      } else {
        checkoutBtn.innerHTML = originalHTML;
      }

      // Proceed to checkout page
      if (checkoutBtn.tagName === 'A') {
        window.location.href = checkoutBtn.href;
      } else {
        const form = checkoutBtn.closest('form');
        if (form) {
          form.submit();
        } else {
          window.location.href = '/checkout';
        }
      }
    });
  }

  async handleCheckoutCoinsUpdate() {
    const customerId = window.MorazeCoinsConfig?.customerId || this.customerId;
    if (!customerId) return;

    // Fetch current cart state
    let cart = null;
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const cartRes = await fetch(rootUrl + 'cart.js');
      cart = await cartRes.json();
    } catch (e) {
      console.warn('[Moraze Coins] Failed to fetch cart before checkout:', e);
      return;
    }

    if (!cart || !cart.items || cart.items.length === 0) return;

    // 1. Calculate coins to deduct for each Milestone gift in the cart
    let totalCoinsToDeduct = 0;
    cart.items.forEach(item => {
      const isMilestoneGift = item.properties && 
                              item.properties._free_gift === 'true' && 
                              item.properties['Gift Status'] === 'Milestone';
      if (isMilestoneGift) {
        // Read the saved _coins_cost property, fallback to 500 if not found
        const cost = parseInt(item.properties._coins_cost || item.properties['Coins Cost'] || 500, 10);
        totalCoinsToDeduct += cost * item.quantity;
      }
    });

    // 2. Calculate coins earned based on cart total: ₹100 product purchase = 100 Coins (₹1 = 1 Coin)
    // We convert cart total price from cents to Rupees
    const cartTotalRupees = Math.floor(cart.total_price / 100);
    const coinsEarned = cartTotalRupees;

    if (totalCoinsToDeduct > 0 || coinsEarned > 0) {
      const currentBalance = parseInt(localStorage.getItem('moraze_coins_balance') || window.MorazeCoinsConfig?.coins || 0, 10);
      const newBalance = Math.max(0, currentBalance - totalCoinsToDeduct + coinsEarned);

      // Save updated balance to local storage and global config
      localStorage.setItem('moraze_coins_balance', newBalance);
      if (window.MorazeCoinsConfig) {
        window.MorazeCoinsConfig.coins = newBalance;
      }
      this.balance = newBalance;

      console.log(`🪙 Checkout balance update. Earned: +${coinsEarned} coins (from ₹${cartTotalRupees}), Deducted: -${totalCoinsToDeduct} coins. New balance: ${newBalance}`);

      // Call backend API to sync balance if configured
      const BASE_URL = 'BACKEND_URL_HERE';
      if (BASE_URL !== 'BACKEND_URL_HERE') {
        try {
          await fetch(`${BASE_URL}/update-balance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: customerId,
              deducted: totalCoinsToDeduct,
              earned: coinsEarned,
              new_balance: newBalance
            })
          });
        } catch (e) {
          console.warn('[Moraze Coins] Backend balance sync failed:', e);
        }
      }
    }
  }

  // Load and populate the Account dashboard tab
  async loadAccountDashboard() {
    const BASE_URL = 'BACKEND_URL_HERE';
    // Backend person se URL lo
    // aur yahan replace karo

    const customerId = window.MorazeCoinsConfig?.customerId;
    const root = document.getElementById('moraze-coins-account-root');
    const customerName = window.MorazeCoinsConfig?.customerName || root?.getAttribute('data-customer-name') || 'Customer';

    fetch(`${BASE_URL}/balance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customer_id: customerId
      })
    })
    .then(res => res.json())
    .then(data => {
      renderCoinsUI({
        customer_name: customerName,
        coins: data.coins || 0,
        earned_month: data.earned_month || 0,
        used_month: data.used_month || 0,
        transactions: data.transactions || []
      });
    })
    .catch(err => {
      console.error('Balance fetch failed:', err);
      const localBalance = localStorage.getItem('moraze_coins_balance');
      const finalCoins = localBalance !== null ? parseInt(localBalance, 10) : (window.MorazeCoinsConfig?.coins || 0);
      renderCoinsUI({
        customer_name: customerName,
        coins: finalCoins,
        earned_month: 0,
        used_month: 0,
        transactions: []
      });
    });
  }

  // Load and render Cart Checkbox integration
  async loadCartWidget() {
    const container = document.getElementById('moraze-coins-cart-root');
    if (!container) return;

    // Use dynamic balance config values if available
    const localBalance = localStorage.getItem('moraze_coins_balance');
    if (localBalance !== null) {
      this.balance = parseInt(localBalance, 10);
    } else {
      this.balance = window.MorazeCoinsConfig?.coins || 0;
    }
    this.currencyValue = this.balance / 100;

    this.renderCartWidget(container);
  }

  // Render Cart Redemption Widget
  renderCartWidget(container) {
    // Check if we already have an active redemption coupon in localStorage
    const savedCoupon = localStorage.getItem('moraze_coins_applied_coupon');
    const isChecked = !!savedCoupon;

    container.innerHTML = `
      <div class="mc-cart-widget p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 my-4">
        <div class="flex items-start gap-3">
          <input 
            type="checkbox" 
            id="mc-redeem-checkbox" 
            class="w-5 h-5 accent-amber-500 rounded border-gray-300 mt-0.5 cursor-pointer"
            ${isChecked ? 'checked' : ''}
          />
          <div class="flex-1">
            <label for="mc-redeem-checkbox" class="block text-sm font-bold text-gray-900 cursor-pointer">
              Use Moraze Coins (M.C.)
            </label>
            <p class="text-xs text-gray-600 mt-1">
              You have <span class="font-bold text-amber-600">${this.balance.toLocaleString()} M.C.</span> 
              equivalent to <span class="font-bold text-amber-600">₹${this.currencyValue.toFixed(2)}</span>.
            </p>
            
            <div id="mc-coupon-status" class="mt-2 text-xs font-semibold text-emerald-600 flex items-center gap-1.5 ${isChecked ? '' : 'hidden'}">
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Applied Coupon: <span id="mc-applied-code-text" class="font-mono bg-emerald-50 text-emerald-700 border border-emerald-200/50 px-1.5 py-0.5 rounded">${savedCoupon || ''}</span> (Saved ₹${this.currencyValue.toFixed(2)})
            </div>
            
            <div id="mc-widget-spinner" class="mt-2 hidden">
              <span class="inline-block animate-spin w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full"></span>
              <span class="text-xs text-gray-500 ml-1">Applying discount to checkout...</span>
            </div>
          </div>
        </div>
      </div>
    `;

    const checkbox = document.getElementById('mc-redeem-checkbox');
    checkbox.addEventListener('change', async (e) => {
      if (e.target.checked) {
        await this.applyDiscountAction();
      } else {
        await this.removeDiscountAction();
      }
    });

    // Auto-update checkout forms to inject discount parameter
    if (isChecked) {
      this.injectDiscountToCheckoutForm(savedCoupon);
    }
  }

  // Action triggered when "Use Coins" checkbox is ticked
  async applyDiscountAction() {
    const spinner = document.getElementById('mc-widget-spinner');
    const statusDiv = document.getElementById('mc-coupon-status');
    const codeSpan = document.getElementById('mc-applied-code-text');
    const checkbox = document.getElementById('mc-redeem-checkbox');

    checkbox.disabled = true;
    spinner.classList.remove('hidden');

    try {
      // Mock API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      const coupon = "MOCKCOIN485";
      const txId = "mock_tx_123456";

      // Store coupon details locally to handle unchecking later
      localStorage.setItem('moraze_coins_applied_coupon', coupon);
      localStorage.setItem('moraze_coins_applied_transaction', txId);

      // Inject the discount parameter into all cart checkout forms
      this.injectDiscountToCheckoutForm(coupon);

      // Call standard Shopify storefront /discount/ endpoint silently to register in session
      try {
        await fetch(`/discount/${coupon}`);
      } catch (e) {
        console.warn('Silent discount register failed:', e);
      }

      codeSpan.textContent = coupon;
      statusDiv.classList.remove('hidden');
      
      console.log(`✔ Coupon ${coupon} applied successfully!`);
    } catch (e) {
      console.error('Error generating checkout coupon:', e);
      checkbox.checked = false;
      alert('Failed to redeem coins. Please check your network connection.');
    } finally {
      spinner.classList.add('hidden');
      checkbox.disabled = false;
    }
  }

  // Action triggered when "Use Coins" checkbox is unticked
  async removeDiscountAction() {
    const spinner = document.getElementById('mc-widget-spinner');
    const statusDiv = document.getElementById('mc-coupon-status');
    const checkbox = document.getElementById('mc-redeem-checkbox');

    const txId = localStorage.getItem('moraze_coins_applied_transaction');

    if (!txId) {
      // Just clear local state if no transaction exists
      localStorage.removeItem('moraze_coins_applied_coupon');
      statusDiv.classList.add('hidden');
      return;
    }

    checkbox.disabled = true;
    spinner.classList.remove('hidden');

    try {
      // Mock API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // Clear locally stored data
      localStorage.removeItem('moraze_coins_applied_coupon');
      localStorage.removeItem('moraze_coins_applied_transaction');

      // Clear discount parameters from checkout forms
      this.clearDiscountFromCheckoutForm();

      // Clear storefront session discount
      try {
        await fetch('/discount/CLEAR');
      } catch (e) {
        console.warn('Silent discount clear failed:', e);
      }

      statusDiv.classList.add('hidden');
      console.log('↩️ Coins refunded and coupon cleared successfully.');
    } catch (e) {
      console.error('Error clearing checkout coupon:', e);
      checkbox.checked = true; // Rollback check state
      alert('Failed to remove discount. Please try again.');
    } finally {
      spinner.classList.add('hidden');
      checkbox.disabled = false;
    }
  }

  // Modifies checkout action URLs to attach &discount=CODE parameter (Standard Shopify feature)
  injectDiscountToCheckoutForm(coupon) {
    // 1. Handle form submissions
    const forms = document.querySelectorAll('form[action="/cart"], form[action="/checkout"]');
    forms.forEach(form => {
      // Check if a discount input already exists
      let discountInput = form.querySelector('input[name="discount"]');
      if (!discountInput) {
        discountInput = document.createElement('input');
        discountInput.type = 'hidden';
        discountInput.name = 'discount';
        form.appendChild(discountInput);
      }
      discountInput.value = coupon;
    });

    // 2. Handle direct anchor tags
    const checkoutLinks = document.querySelectorAll('a[href*="/checkout"]');
    checkoutLinks.forEach(link => {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('discount', coupon);
      link.href = url.pathname + url.search;
    });
  }

  // Clears attached discount inputs and checkout query variables
  clearDiscountFromCheckoutForm() {
    const inputs = document.querySelectorAll('input[name="discount"]');
    inputs.forEach(input => input.remove());

    const checkoutLinks = document.querySelectorAll('a[href*="/checkout"]');
    checkoutLinks.forEach(link => {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.delete('discount');
      link.href = url.pathname + url.search;
    });
  }

  // Scenario 2: Delegated Event Listener for Coins Login Clicks
  bindLoginListeners() {
    document.addEventListener('click', (e) => {
      const loginTrigger = e.target.closest('.mwc-login-btn, .mz-promo-btn, .mc-login-link');
      if (loginTrigger) {
        if (window.MorazeCoinsConfig?.customLogin) {
          e.preventDefault();
          this.showPremiumLoginModal();
        }
      }
    });
  }

  // Inject Styles for the Premium Glassmorphic Modal
  injectModalStyles() {
    if (document.getElementById('mwc-modal-styles')) return;

    const style = document.createElement('style');
    style.id = 'mwc-modal-styles';
    style.textContent = `
      .mwc-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(15, 10, 10, 0.75);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100000;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s ease;
        padding: 20px;
        box-sizing: border-box;
      }
      .mwc-modal-overlay.active {
        opacity: 1;
        pointer-events: auto;
      }
      .mwc-modal-container {
        background: linear-gradient(145deg, #261114 0%, #12090a 100%);
        border: 1px solid rgba(212, 175, 55, 0.25);
        border-radius: 24px;
        width: 100%;
        max-width: 440px;
        padding: 36px 30px;
        position: relative;
        box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), inset 0 1px 1px rgba(255, 255, 255, 0.1);
        transform: translateY(30px) scale(0.96);
        transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        box-sizing: border-box;
        color: #fdfaf9;
        text-align: center;
      }
      .mwc-modal-overlay.active .mwc-modal-container {
        transform: translateY(0) scale(1);
      }
      .mwc-modal-close {
        position: absolute;
        top: 18px;
        right: 18px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: #ebdcd5;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 20px;
        line-height: 1;
        transition: all 0.2s ease;
      }
      .mwc-modal-close:hover {
        background: rgba(255, 255, 255, 0.15);
        color: #ffffff;
        transform: rotate(90deg);
      }
      .mwc-modal-icon {
        font-size: 44px;
        margin-bottom: 16px;
        display: inline-block;
        filter: drop-shadow(0 4px 8px rgba(212, 175, 55, 0.3));
        animation: floatCoin 3s ease-in-out infinite;
      }
      @keyframes floatCoin {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-6px); }
      }
      .mwc-modal-title {
        font-family: 'Inter', sans-serif;
        font-size: 24px;
        font-weight: 800;
        letter-spacing: -0.5px;
        color: #ffffff;
        margin: 0 0 8px 0;
        background: linear-gradient(135deg, #ffffff 60%, #f7d580 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      .mwc-modal-desc {
        font-size: 13px;
        color: #c9b9b5;
        line-height: 1.5;
        margin: 0 0 24px 0;
      }
      .mwc-modal-form {
        text-align: left;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .mwc-form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .mwc-form-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #d4af37;
      }
      .mwc-form-input {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        padding: 12px 16px;
        color: #ffffff;
        font-size: 14px;
        transition: all 0.2s ease;
        width: 100%;
        box-sizing: border-box;
      }
      .mwc-form-input:focus {
        outline: none;
        border-color: #d4af37;
        background: rgba(255, 255, 255, 0.08);
        box-shadow: 0 0 8px rgba(212, 175, 55, 0.25);
      }
      .mwc-submit-btn {
        background: linear-gradient(135deg, #d4af37 0%, #b5921f 100%);
        color: #12090a;
        border: none;
        border-radius: 8px;
        padding: 14px 20px;
        font-size: 14px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        cursor: pointer;
        transition: all 0.25s ease;
        margin-top: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }
      .mwc-submit-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(212, 175, 55, 0.3);
        filter: brightness(1.1);
      }
      .mwc-submit-btn:disabled {
        background: #554422;
        color: #887755;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }
      .mwc-error-msg {
        background: rgba(239, 68, 68, 0.15);
        border: 1px solid rgba(239, 68, 68, 0.4);
        color: #fca5a5;
        font-size: 12px;
        padding: 10px 14px;
        border-radius: 8px;
        text-align: center;
        display: none;
        animation: slideDown 0.25s ease;
      }
      @keyframes slideDown {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .mwc-modal-footer {
        margin-top: 20px;
        font-size: 12px;
        color: #9c8984;
      }
      .mwc-modal-footer a {
        color: #d4af37;
        text-decoration: none;
        font-weight: 600;
        transition: color 0.15s ease;
      }
      .mwc-modal-footer a:hover {
        color: #ffffff;
      }
      .mwc-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid rgba(18, 9, 10, 0.2);
        border-top-color: #12090a;
        border-radius: 50%;
        animation: mwc-spin 0.6s linear infinite;
      }
      @keyframes mwc-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  // Create and show the premium modal overlay
  showPremiumLoginModal() {
    let overlay = document.getElementById('mwc-login-modal');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'mwc-login-modal';
      overlay.className = 'mwc-modal-overlay';
      overlay.innerHTML = `
        <div class="mwc-modal-container">
          <button class="mwc-modal-close" aria-label="Close modal">&times;</button>
          <div class="mwc-modal-icon">🪙</div>
          <h3 class="mwc-modal-title">Moraze Loyalty Wallet</h3>
          <p class="mwc-modal-desc">Access your Moraze Coins balance, track ledger transactions, and get instant checkout discounts.</p>
          
          <div id="mwc-modal-error" class="mwc-error-msg"></div>

          <form id="mwc-modal-login-form" class="mwc-modal-form">
            <div class="mwc-form-group">
              <label for="mwc-email" class="mwc-form-label">Email Address</label>
              <input type="email" id="mwc-email" class="mwc-form-input" placeholder="you@example.com" required autocomplete="email" />
            </div>
            
            <div class="mwc-form-group">
              <label for="mwc-password" class="mwc-form-label">Password</label>
              <input type="password" id="mwc-password" class="mwc-form-input" placeholder="••••••••" required autocomplete="current-password" />
            </div>

            <button type="submit" id="mwc-submit-btn" class="mwc-submit-btn">
              <span>Secure Login</span>
            </button>
          </form>

          <div class="mwc-modal-footer">
            New to Moraze? <a href="/account/register">Create an account</a>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);

      // Bind close events
      overlay.querySelector('.mwc-modal-close').addEventListener('click', () => this.closePremiumLoginModal());
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) this.closePremiumLoginModal();
      });

      // Bind form submit
      const form = document.getElementById('mwc-modal-login-form');
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = document.getElementById('mwc-email').value;
        const password = document.getElementById('mwc-password').value;
        await this.handleCustomLoginSubmit(email, password);
      });
    }

    // Reset form states
    document.getElementById('mwc-modal-error').style.display = 'none';
    document.getElementById('mwc-modal-login-form').reset();

    // Trigger reflow for transition
    void overlay.offsetWidth;
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden'; // prevent page scroll
  }

  // Close modal
  closePremiumLoginModal() {
    const overlay = document.getElementById('mwc-login-modal');
    if (overlay) {
      overlay.classList.remove('active');
      document.body.style.overflow = ''; // restore page scroll
    }
  }

  // Handle AJAX request to backend for Scenario 2
  async handleCustomLoginSubmit(email, password) {
    const submitBtn = document.getElementById('mwc-submit-btn');
    const errorMsg = document.getElementById('mwc-modal-error');
    
    // Disable submit button and add loader
    submitBtn.disabled = true;
    const originalBtnHTML = submitBtn.innerHTML;
    submitBtn.innerHTML = `<span class="mwc-spinner"></span> <span>Verifying...</span>`;
    errorMsg.style.display = 'none';

    try {
      // Mock auth delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Simulate success reload (since it's mock frontend only)
      window.location.reload();
    } catch (err) {
      console.error('Custom auth failed:', err);
      errorMsg.textContent = err.message || 'Error connecting to loyalty server.';
      errorMsg.style.display = 'block';
      
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalBtnHTML;
    }
  }
}

// Initialise storefront loyalty engine
document.addEventListener('DOMContentLoaded', () => {
  window.MorazeCoins = new MorazeCoinsController();
});

function renderMilestone(coins) {
  const milestones = [
    {
      coins: 1000,
      reward: 'Free Nail Polish'
    },
    {
      coins: 2000,
      reward: 'Free Stick Lipstick'
    },
    {
      coins: 2500,
      reward: 'Free Liquid Lipstick'
    }
  ];

  const next = milestones.find(
    m => m.coins > coins
  );

  if (!next) return `
    <div style="
      background:#EAF3DE;
      border-radius:10px;
      padding:16px;
      text-align:center;
      margin-bottom:16px;
    ">
      <p style="color:#3B6D11;
        font-weight:500;
        margin:0;">
        All milestones achieved!
      </p>
    </div>
  `;

  const prev = milestones[
    milestones.indexOf(next) - 1
  ];
  const start = prev ? prev.coins : 0;
  const progress = 
    ((coins - start) / 
    (next.coins - start)) * 100;
  const remaining = next.coins - coins;

  return `
  <div style="
    background:#fff;
    border:1px solid #eee;
    border-radius:10px;
    padding:16px;
    margin-bottom:16px;
  ">
    <div style="
      display:flex;
      justify-content:space-between;
      margin-bottom:8px;
    ">
      <p style="
        font-size:13px;
        color:#888;
        margin:0;
      ">Next reward</p>
      <p style="
        font-size:13px;
        font-weight:500;
        color:#D4537E;
        margin:0;
      ">${next.reward}</p>
    </div>
    <div style="
      background:#F4C0D1;
      border-radius:20px;
      height:8px;
      margin-bottom:8px;
    ">
      <div style="
        background:#D4537E;
        border-radius:20px;
        height:8px;
        width:${Math.min(progress,100)}%;
        transition: width 0.5s ease;
      "></div>
    </div>
    <p style="
      font-size:12px;
      color:#993556;
      text-align:center;
      margin:0;
    ">
      Aur ${remaining} coins mein 
      ${next.reward} milega!
    </p>
  </div>
  `;
}

function renderCoinsUI(data) {
  const root = document.getElementById(
    'moraze-coins-account-root'
  );
  if (!root) return;

  root.innerHTML = `
  <div style="
    font-family: sans-serif;
    max-width: 480px;
    margin: 0 auto;
    padding: 16px;
  ">

    <!-- HEADER -->
    <div style="
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    ">
      <div>
        <p style="
          font-size: 13px;
          color: #888;
          margin-bottom: 4px;
        ">Welcome back</p>
        <h2 style="
          font-size: 18px;
          font-weight: 500;
          margin: 0;
        ">${data.customer_name}</h2>
      </div>
      <span style="
        background: #FBEAF0;
        color: #993556;
        font-size: 12px;
        padding: 4px 14px;
        border-radius: 20px;
        font-weight: 500;
      ">Moraze Coins</span>
    </div>

    <!-- BALANCE CARD -->
    <div style="
      background: #FBEAF0;
      border: 1px solid #F4C0D1;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 16px;
    ">
      <p style="
        font-size: 13px;
        color: #993556;
        margin-bottom: 8px;
      ">Total coins balance</p>
      <div style="
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin-bottom: 4px;
      ">
        <span style="
          font-size: 36px;
          font-weight: 500;
          color: #D4537E;
        ">${data.coins}</span>
        <span style="
          font-size: 14px;
          color: #993556;
        ">coins</span>
      </div>
      <p style="
        font-size: 13px;
        color: #993556;
        margin-bottom: 4px;
      ">= <strong>
        ₹${(data.coins / 100).toFixed(2)}
      </strong> redeemable value</p>
      <p style="
        font-size: 11px;
        color: #72243E;
      ">100 coins = ₹1</p>
    </div>

    ${renderMilestone(data.coins)}

    <!-- STATS GRID -->
    <div style="
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 16px;
    ">
      <div style="
        background: #fff;
        border: 1px solid #eee;
        border-radius: 10px;
        padding: 16px;
        text-align: center;
      ">
        <p style="
          font-size: 12px;
          color: #888;
          margin-bottom: 6px;
        ">Earned this month</p>
        <p style="
          font-size: 20px;
          font-weight: 500;
        ">${data.earned_month}</p>
      </div>
      <div style="
        background: #fff;
        border: 1px solid #eee;
        border-radius: 10px;
        padding: 16px;
        text-align: center;
      ">
        <p style="
          font-size: 12px;
          color: #888;
          margin-bottom: 6px;
        ">Used this month</p>
        <p style="
          font-size: 20px;
          font-weight: 500;
        ">${data.used_month}</p>
      </div>
    </div>

    <!-- BUTTONS -->
    <div style="
      display: flex;
      gap: 12px;
      margin-bottom: 20px;
    ">
      <button onclick="
        if (typeof window.openCartDrawer === 'function') {
          window.openCartDrawer();
        } else {
          window.location.href = '/#cart-open';
        }
      " style="
        flex: 1;
        background: #D4537E;
        color: #fff;
        border: none;
        border-radius: 8px;
        padding: 12px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
      ">Use Coins</button>

      <button onclick="
        window.location.href='/collections/all'
      " style="
        flex: 1;
        background: transparent;
        color: #D4537E;
        border: 1px solid #D4537E;
        border-radius: 8px;
        padding: 12px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
      ">Withdraw</button>
    </div>

    <!-- TRANSACTION HISTORY -->
    <div style="
      background: #fff;
      border: 1px solid #eee;
      border-radius: 12px;
      padding: 16px;
    ">
      <p style="
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 16px;
      ">Transaction history</p>

      <div id="mc-history-list"></div>
    </div>

    <!-- FOOTER -->
    <p style="
      font-size: 11px;
      color: #aaa;
      text-align: center;
      margin-top: 16px;
    ">Coins credited only after delivery</p>

  </div>
  `;

  renderHistory(data.transactions);
}

function renderHistory(transactions) {
  const list = document.getElementById(
    'mc-history-list'
  );
  if (!list) return;

  if (!transactions || 
      transactions.length === 0) {
    list.innerHTML = `
      <p style="
        font-size: 13px;
        color: #aaa;
        text-align: center;
        padding: 20px 0;
      ">No transactions yet</p>
    `;
    return;
  }

  list.innerHTML = transactions.map(
    (txn, i) => {

    let badge = '';
    let coinsText = '';
    let iconBg = '';
    let iconColor = '';
    let icon = '';

    if (txn.type === 'redeemed') {
      badge = `<span style="
        background:#FBEAF0;
        color:#993556;
        font-size:11px;
        padding:2px 8px;
        border-radius:20px;
      ">Used</span>`;
      coinsText = `-${txn.coins}`;
      iconBg = '#FBEAF0';
      iconColor = '#993556';
      icon = '-';
    } else if (txn.status === 'delivered') {
      badge = `<span style="
        background:#EAF3DE;
        color:#3B6D11;
        font-size:11px;
        padding:2px 8px;
        border-radius:20px;
      ">Credited</span>`;
      coinsText = `+${txn.coins}`;
      iconBg = '#EAF3DE';
      iconColor = '#3B6D11';
      icon = '+';
    } else if (txn.status === 'cancelled') {
      badge = `<span style="
        background:#FCEBEB;
        color:#A32D2D;
        font-size:11px;
        padding:2px 8px;
        border-radius:20px;
      ">Cancelled</span>`;
      coinsText = `${txn.coins}`;
      iconBg = '#FCEBEB';
      iconColor = '#A32D2D';
      icon = 'x';
    } else {
      badge = `<span style="
        background:#FAEEDA;
        color:#854F0B;
        font-size:11px;
        padding:2px 8px;
        border-radius:20px;
      ">Pending</span>`;
      coinsText = `+${txn.coins}`;
      iconBg = '#FAEEDA';
      iconColor = '#854F0B';
      icon = '~';
    }

    return `
    <div style="
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 0;
      ${i > 0 ? 
        'border-top: 1px solid #eee;' : 
        ''}
    ">
      <div style="
        display: flex;
        align-items: center;
        gap: 10px;
      ">
        <div style="
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: ${iconBg};
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${iconColor};
          font-weight: 500;
          font-size: 16px;
        ">${icon}</div>
        <div>
          <p style="
            font-size: 13px;
            font-weight: 500;
            margin-bottom: 2px;
          ">Order #${txn.order_id}</p>
          <p style="
            font-size: 11px;
            color: #888;
          ">${txn.date}</p>
        </div>
      </div>
      <div style="text-align: right;">
        <p style="
          font-size: 13px;
          font-weight: 500;
          color: ${iconColor};
          margin-bottom: 4px;
        ">${coinsText} coins</p>
        ${badge}
      </div>
    </div>
    `;
  }).join('');
}